/**
 * Sample Quick Buy - collection grid sample button
 *
 * Mirrors the PDP sample flow (sample-flow.js) on collection cards:
 *   - member with credit  -> free add (claim, cookie, add, attach)
 *   - member, no credit   -> paid add
 *   - new visitor         -> email capture sheet, then free add
 *
 * All cart and discount work is delegated to sample-core.js so the ordering
 * stays identical to the PDP. One sheet is shared by the whole grid and is
 * contextualised with whichever card was pressed.
 */
(function () {
  'use strict';

  var core = window.knpSampleCore;
  if (!core) {
    console.error('sample-quick-buy.js: sample-core.js must load first');
    return;
  }

  var SHEET_ID = 'quickbuy';

  function QuickBuy(root) {
    this.root = root;
    this.sheetOverlay = document.getElementById('SampleSheet-' + SHEET_ID);
    this.sheetForm = document.getElementById('SampleSheetForm-' + SHEET_ID);
    this.sheetSuccess = document.getElementById('SampleSheetSuccess-' + SHEET_ID);
    this.sheetPaid = document.getElementById('SampleSheetPaid-' + SHEET_ID);
    this.emailInput = document.getElementById('SampleEmail-' + SHEET_ID);
    this.emailError = document.getElementById('SampleEmailError-' + SHEET_ID);
    this.toast = document.getElementById('SampleToast-' + SHEET_ID);

    // State. hasCredit is DERIVED (serverCredit && no sample in cart), never
    // set directly: a one-way flag went stale the moment the customer removed
    // the sample from the drawer or navigated here with one already in cart.
    this.serverCredit = true;      // what Netlify says the member has left
    this.anySampleInCart = false;  // any sample variant, any product
    this.cartSampleVariantIds = {}; // variant id -> true, for duplicate guard
    this.hasCredit = true;
    this.creditChecked = false;
    this.submitting = false;
    this.discountCode = null;
    this.active = null;      // data payload for the card being acted on
    this.lastFocus = null;

    this.init();
  }

  QuickBuy.prototype.init = function () {
    this.bindEvents();
    this.checkIdentity();
  };

  QuickBuy.prototype.deriveCredit = function () {
    this.hasCredit = this.serverCredit && !this.anySampleInCart;
  };

  /* ── Identity ──
   * Members get their remaining credit resolved once per page so the buttons
   * can advertise FREE rather than the £2 price. No variant is passed: the
   * grid has many, so there is nothing to pre-scope the price rule to yet.
   */
  QuickBuy.prototype.checkIdentity = function () {
    var self = this;

    // New visitors skip the Netlify credit check (serverCredit stays true:
    // their first sample is free via signup) but still need the cart check
    // and label paint, otherwise the server-rendered £2 price stands.
    if (!core.isMember() || !core.getMemberEmail()) {
      this.checkCart()
        .then(function () { self.deriveCredit(); })
        .finally(function () {
          self.creditChecked = true;
          self.updateButtonLabels();
        });
      return;
    }

    var creditPromise = this.fetchServerCredit();

    // Credit is only spent at checkout, so the server still reports it while
    // a free sample sits unpaid in the cart. Without the cart side of the
    // derivation the buttons flip back to FREE on reload and the next add
    // quietly costs £2.
    var cartPromise = this.checkCart();

    Promise.all([creditPromise, cartPromise])
      .then(function () {
        self.deriveCredit();
      })
      .finally(function () {
        self.creditChecked = true;
        self.updateButtonLabels();
      });
  };

  /**
   * Ask Netlify how much credit the member has left. Fails OPEN (credit
   * assumed) to match the PDP flow's fallback: on the PDP the badge and these
   * buttons render side by side, and opposite failure defaults had the pill
   * saying FREE above related-product cards saying £2. The drawer price is
   * the source of truth either way. A failed check is retried on the next
   * cart recheck, since cold Netlify regularly outlives the timeout.
   */
  QuickBuy.prototype.fetchServerCredit = function () {
    var self = this;
    return core.withTimeout(core.netlifyCheckCredit(core.getMemberEmail()), 3000)
      .then(function (result) {
        if (result && result.discount_code) self.discountCode = result.discount_code;
        self.serverCredit = !result || result.credits_remaining > 0;
        self.creditCheckFailed = false;
      })
      .catch(function () {
        self.serverCredit = true;
        self.creditCheckFailed = true;
      });
  };

  /**
   * Refresh cart-derived state. Samples are identified by variant title,
   * matching the PDP's detection, since they span many products.
   */
  QuickBuy.prototype.checkCart = function () {
    var self = this;
    // no-store: on back/forward navigations the browser happily serves
    // /cart.js from HTTP cache, which painted "Added" from a cart state that
    // no longer existed. Cart truth must always come from the network.
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var ids = {};
        var any = false;
        (cart.items || []).forEach(function (item) {
          var title = (item.variant_title || item.title || '').toLowerCase();
          if (title.indexOf('sample') > -1 ||
              title.indexOf('100ml') > -1 ||
              title.indexOf('tester') > -1 ||
              title.indexOf('a6') > -1) {
            any = true;
            ids[item.variant_id] = true;
          }
        });
        self.anySampleInCart = any;
        self.cartSampleVariantIds = ids;
        self.deriveCredit();
        self.updateFloatingBasket(cart.item_count);
        return any;
      })
      .catch(function () {
        // Network error: keep current state rather than guessing
        return self.anySampleInCart;
      });
  };

  /**
   * Cart changed (add, remove, quantity, drawer edit): re-derive and repaint.
   * This is what lets the labels recover to FREE when the customer removes
   * their free sample, and go paid when the PDP flow on the same page adds one
   * to a related-products card's grid.
   */
  QuickBuy.prototype.recheckCart = function () {
    var self = this;
    clearTimeout(this._recheckTimer);
    this._recheckTimer = setTimeout(function () {
      // Piggy-back a credit retry when the init check timed out on a cold
      // Netlify start, so a transient failure does not stick for the page life
      var credit = (core.isMember() && self.creditCheckFailed)
        ? self.fetchServerCredit()
        : Promise.resolve();
      Promise.all([credit, self.checkCart()]).then(function () {
        self.deriveCredit();
        self.updateButtonLabels();
      });
    }, 150);
  };

  /**
   * Drives the price label in BOTH directions. Once the credit is spent the
   * buttons must fall back to the real price: leaving them on FREE would
   * promise a free sample and then charge £2 for it.
   *
   * New visitors see FREE too, matching the PDP pill's badge: their first
   * sample IS free once they sign up, and the sheet the button opens is the
   * signup. Only a sample already in the cart (or a member with spent
   * credit) drops the label to the paid price.
   */
  QuickBuy.prototype.updateButtonLabels = function () {
    var free = this.hasCredit;
    var idsInCart = this.cartSampleVariantIds;

    var wrappers = document.querySelectorAll('.product-card-sample');
    Array.prototype.forEach.call(wrappers, function (wrapper) {
      var button = wrapper.querySelector('[data-sample-quick-buy]');
      if (!button) return;

      var priceEl = button.querySelector('[data-sample-price]');
      if (!priceEl) return;

      // Read the payload rather than trusting button.disabled: a button that
      // is mid-add is also disabled, and skipping it leaves the one button the
      // customer just pressed showing a stale FREE label.
      var data = null;
      var script = wrapper.querySelector('[data-sample-quick-buy-data]');
      if (script) {
        try { data = JSON.parse(script.textContent); } catch (e) {}
      }
      if (data && data.available === false) return;

      var labelEl = button.querySelector('[data-sample-label]');

      // THIS card's sample already in the basket: quiet confirmation state
      if (data && idsInCart[data.variantId]) {
        if (labelEl) labelEl.textContent = 'Added';
        priceEl.textContent = '';
        button.classList.add('product-card-sample__button--added');
        button.classList.remove('product-card-sample__button--free');
        button.setAttribute('aria-label', 'Sample of ' + data.productTitle + ' is already in your basket');
        return;
      }
      button.classList.remove('product-card-sample__button--added');
      if (data && data.productTitle) {
        button.setAttribute('aria-label', 'Add a sample of ' + data.productTitle + ' to your basket');
      }

      if (free) {
        if (labelEl) labelEl.textContent = 'Add free sample';
        priceEl.textContent = '';
        button.classList.add('product-card-sample__button--free');
        return;
      }

      // Restore from the card's own payload rather than a cached string, so
      // this stays correct on grids where samples are not all the same price
      if (labelEl) labelEl.textContent = 'Add sample';
      if (data && data.priceFormatted) priceEl.textContent = data.priceFormatted;
      button.classList.remove('product-card-sample__button--free');
    });
  };

  /* ── Card data ── */

  QuickBuy.prototype.getCardData = function (button) {
    var wrapper = button.closest('.product-card-sample');
    if (!wrapper) return null;

    var script = wrapper.querySelector('[data-sample-quick-buy-data]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('sample-quick-buy: bad card payload', e);
      return null;
    }
  };

  /* ── Events ── */

  QuickBuy.prototype.bindEvents = function () {
    var self = this;

    // Delegated so paginated/filtered grids keep working after a re-render
    document.addEventListener('click', function (e) {
      var button = e.target.closest('[data-sample-quick-buy]');
      if (button && !button.disabled) {
        e.preventDefault();
        self.onButtonClick(button);
        return;
      }

      if (!self.sheetOverlay || self.sheetOverlay.hidden) return;
      if (!self.sheetOverlay.contains(e.target)) return;

      var action = e.target.closest('[data-action]');
      if (!action) return;
      var name = action.dataset.action;

      if (name === 'close-sheet') {
        e.preventDefault();
        self.closeSheet();
      } else if (name === 'submit-sample') {
        e.preventDefault();
        self.onSubmitEmail();
      } else if (name === 'skip-offer') {
        e.preventDefault();
        self.onSkipOffer();
      } else if (name === 'go-cart') {
        e.preventDefault();
        // Let any in-flight discount heal land first (capped) so the basket
        // page arrives already showing the sample free, not £2 then a flip
        var heal = self.pendingHeal || (window.knpSampleCore && window.knpSampleCore.lastHeal);
        var cap = new Promise(function (r) { setTimeout(r, 1500); });
        Promise.race([Promise.resolve(heal).catch(function () {}), cap]).then(function () {
          window.location.href = window.theme.routes.cart_url || '/cart';
        });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.sheetOverlay && !self.sheetOverlay.hidden) {
        self.closeSheet();
      }
    });

    // Floating basket: opens the drawer (deliberate cart access, unlike the
    // suppressed open-on-add). Falls back to a plain /cart link without JS.
    var floatingBasket = document.getElementById('FloatingBasket');
    if (floatingBasket) {
      floatingBasket.addEventListener('click', function (e) {
        e.preventDefault();
        core.openCartDrawer();
      });
    }

    // Stay in sync with every other way the cart can change: the drawer's
    // remove/quantity controls, the PDP sample flow on this page's related
    // products, the reconciler's rewrites. Same event set the PDP listens to.
    ['cart:item-added', 'cart:refresh', 'line-item:change:end'].forEach(function (name) {
      document.addEventListener(name, function () { self.recheckCart(); });
    });

    // Back/forward cache: navigating back to this page restores the old DOM
    // and JS state without a reload, so labels reflect the cart as it was.
    // pageshow(persisted) is the restore signal; visibilitychange catches the
    // tab-switch equivalent.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) self.recheckCart();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') self.recheckCart();
    });

    // Cards injected after init (AJAX related products, drawer
    // recommendations, filtered grid re-renders) arrive with server-rendered
    // £2 labels and would never be repainted. Watch for them and repaint from
    // current state; debounced via the same timer as recheckCart's paint.
    if (window.MutationObserver) {
      var repaintTimer = null;
      this._cardObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (node.nodeType !== 1) continue;
            if ((node.matches && node.matches('.product-card-sample')) ||
                (node.querySelector && node.querySelector('.product-card-sample'))) {
              clearTimeout(repaintTimer);
              repaintTimer = setTimeout(function () { self.updateButtonLabels(); }, 100);
              return;
            }
          }
        }
      });
      this._cardObserver.observe(document.body, { childList: true, subtree: true });
    }

    if (this.emailInput) {
      this.emailInput.addEventListener('input', function () { self.clearEmailError(); });
      this.emailInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          self.onSubmitEmail();
        }
      });
    }
  };

  QuickBuy.prototype.onButtonClick = function (button) {
    var data = this.getCardData(button);
    if (!data) return;

    // Same-product duplicate: mirror the PDP's guard rather than quietly
    // charging £2 for a second copy of a sample already in the basket.
    if (this.cartSampleVariantIds[data.variantId]) {
      this.showToast('Sample is already in your basket');
      return;
    }

    this.active = data;
    this.activeButton = button;

    if (core.isMember() && this.hasCredit) {
      this.addSample(true);
    } else if (core.isMember()) {
      this.addSample(false);
    } else {
      this.openSheet();
    }
  };

  /* ── Cart ── */

  QuickBuy.prototype.addSample = function (isFree) {
    if (this.submitting || !this.active) return;

    var self = this;
    var data = this.active;
    this.submitting = true;
    this.setButtonLoading(true);

    // openDrawer false: grid adds confirm in place (Added state + bubble
    // pulse) so multi-sample browsing is not interrupted on every press
    core.addSample(data.variantId, isFree, this.discountCode, false)
      .then(function () {
        // The sample is now in the cart, so the derivation flips every
        // button to paid (cart:item-added also lands here via recheckCart)
        self.anySampleInCart = true;
        self.cartSampleVariantIds[data.variantId] = true;
        self.deriveCredit();
        self.updateButtonLabels();
        self.pulseCartBubble();
        self.tagCampaign();
        if (isFree && window.knpReconcile) window.knpReconcile(0);
      })
      .catch(function (err) {
        self.showToast('Something went wrong. Please try again.');
        console.error('Sample quick buy failed:', err);
      })
      .finally(function () {
        self.submitting = false;
        self.setButtonLoading(false);
      });
  };

  /**
   * Stamp the cart with which campaign page the sample came from.
   *
   * Cart attributes carry through to the order, where they appear under
   * Additional details in admin and in order exports, so sample orders can
   * be attributed to a landing page without relying on session analytics.
   * Written once per cart: the first campaign touch is the one that earned
   * the order, and later adds should not overwrite it.
   */
  QuickBuy.prototype.tagCampaign = function () {
    var root = document.getElementById('SampleQuickBuy');
    var campaign = root && root.dataset.campaign;
    if (!campaign || this.campaignTagged) return Promise.resolve();
    this.campaignTagged = true;

    var attributes = { 'Sample campaign': campaign };

    // Carry ad parameters through so orders can be attributed per ad set,
    // not just per landing page.
    var params = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(function (key) {
      var value = params.get(key);
      if (value) attributes[key] = value;
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      credentials: 'same-origin'
    }).catch(function () {
      // Attribution is reporting metadata: never block or fail the add
    });
  };

  /**
   * The button never goes textless: while the add is in flight it reads
   * "Adding" with an inline spinner, then resolves straight to its real
   * state ("Added" / price). Coming out of loading always repaints from
   * current state, which also restores the label after a failed add.
   */
  QuickBuy.prototype.setButtonLoading = function (loading) {
    if (!this.activeButton) return;
    var button = this.activeButton;
    button.classList.toggle('product-card-sample__button--loading', !!loading);
    button.disabled = !!loading;

    if (loading) {
      var labelEl = button.querySelector('[data-sample-label]');
      var priceEl = button.querySelector('[data-sample-price]');
      if (labelEl) labelEl.textContent = 'Adding';
      if (priceEl) priceEl.textContent = '';
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
      this.updateButtonLabels();
    }
  };

  /**
   * Keep the floating basket's count and visibility in step with the cart.
   * Server-rendered on load; this covers every subsequent cart change since
   * all of them funnel through checkCart.
   */
  QuickBuy.prototype.updateFloatingBasket = function (itemCount) {
    var basket = document.getElementById('FloatingBasket');
    if (!basket) return;
    var count = basket.querySelector('[data-floating-count]');
    if (count) count.textContent = itemCount;
    basket.classList.toggle('floating-basket--empty', itemCount === 0);
    basket.setAttribute('aria-label', 'Basket, ' + itemCount + ' items. Review and checkout');
  };

  /**
   * Brief scale pulse on the header cart count, drawing the eye to the
   * incremented number since the drawer no longer opens for grid adds.
   */
  QuickBuy.prototype.pulseCartBubble = function () {
    // Re-query every time: the render swaps the bubble element on each add.
    // Scoped to the header toggle; the drawer holds another .thb-item-count
    // that is hidden while the drawer is closed.
    // Header bubble re-queried every time (the render swaps the element on
    // each add); the floating basket count pulses in step with it.
    var targets = [
      document.querySelector('#cart-drawer-toggle .thb-item-count'),
      document.querySelector('#FloatingBasket [data-floating-count]')
    ];
    targets.forEach(function (bubble) {
      if (!bubble) return;
      bubble.classList.remove('sqb-cart-pulse');
      // force reflow so back-to-back adds restart the animation
      void bubble.offsetWidth;
      bubble.classList.add('sqb-cart-pulse');
      bubble.addEventListener('animationend', function handler() {
        bubble.classList.remove('sqb-cart-pulse');
        bubble.removeEventListener('animationend', handler);
      });
    });
  };

  /* ── Sheet ── */

  QuickBuy.prototype.openSheet = function () {
    if (!this.sheetOverlay || !this.active) return;

    var data = this.active;
    this.lastFocus = document.activeElement;

    // Contextualise the shared sheet with the pressed card
    var swatch = this.sheetOverlay.querySelector('[data-sheet-swatch]');
    if (swatch) {
      if (data.featuredImage) {
        swatch.src = data.featuredImage;
        swatch.alt = data.productTitle;
        swatch.removeAttribute('hidden');
      } else {
        swatch.setAttribute('hidden', '');
      }
    }

    var name = this.sheetOverlay.querySelector('[data-sheet-product-name]');
    if (name) name.textContent = data.productTitle;

    var struck = this.sheetOverlay.querySelector('[data-sheet-struck]');
    if (struck) struck.textContent = data.priceFormatted;

    var paidPrice = this.sheetOverlay.querySelector('[data-sheet-paid-price]');
    if (paidPrice) paidPrice.textContent = data.priceFormatted;

    // Always open on the form state
    this.sheetForm.removeAttribute('hidden');
    this.sheetSuccess.setAttribute('hidden', '');
    this.sheetPaid.setAttribute('hidden', '');
    this.clearEmailError();

    this.sheetOverlay.removeAttribute('hidden');
    this.sheetOverlay.removeAttribute('aria-hidden');

    var self = this;

    // Double rAF: ensures the browser has committed the `hidden` removal and
    // painted a frame before we kick off the transition. This gives iOS Safari
    // a clean paint frame to establish hit-targets for buttons inside the
    // transformed .sf-sheet, which fixes the "first tap does nothing" bug.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.sheetOverlay.classList.add('sf-sheet--open');
      });
    });

    // Prevent body scroll AFTER the transition has had time to begin painting.
    // Applying this synchronously with the overlay reveal cancels in-flight
    // touch-to-click conversions on iOS Safari.
    setTimeout(function () {
      document.body.style.overflow = 'hidden';
    }, 50);

    // Skip autofocus on touch devices: raising the on-screen keyboard mid
    // transition swallows taps on the sheet's other buttons.
    var isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
    if (!isTouchDevice) {
      setTimeout(function () {
        if (self.emailInput) self.emailInput.focus();
      }, 280);
    }

    this.setupFocusTrap();
  };

  QuickBuy.prototype.closeSheet = function () {
    if (!this.sheetOverlay) return;

    var self = this;
    this.sheetOverlay.classList.remove('sf-sheet--open');
    document.body.style.overflow = '';
    this.removeFocusTrap();

    setTimeout(function () {
      self.sheetOverlay.setAttribute('hidden', '');
      self.sheetOverlay.setAttribute('aria-hidden', 'true');
    }, 250);

    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  QuickBuy.prototype.setupFocusTrap = function () {
    var self = this;
    this._focusTrapHandler = function (e) {
      if (e.key !== 'Tab' || self.sheetOverlay.hidden) return;

      var focusable = self.sheetOverlay.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href]'
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this._focusTrapHandler);
  };

  QuickBuy.prototype.removeFocusTrap = function () {
    if (this._focusTrapHandler) {
      document.removeEventListener('keydown', this._focusTrapHandler);
      this._focusTrapHandler = null;
    }
  };

  /* ── Email capture ──
   * Same shape as the PDP path: add to cart optimistically, show success as
   * soon as the add lands, then let Netlify settle and hand to the reconciler
   * so the discount attaches without holding up the UI.
   */
  QuickBuy.prototype.onSubmitEmail = function () {
    if (this.submitting || !this.active) return;

    var email = this.emailInput ? this.emailInput.value.trim() : '';
    if (!core.isValidEmail(email)) {
      this.showEmailError();
      return;
    }

    var self = this;
    var data = this.active;
    this.submitting = true;

    var cta = this.sheetOverlay.querySelector('[data-action="submit-sample"]');
    var ctaText = cta.querySelector('.sf-sheet__cta-text');
    var ctaLoading = cta.querySelector('.sf-sheet__cta-loading');
    cta.disabled = true;
    ctaText.setAttribute('hidden', '');
    ctaLoading.removeAttribute('hidden');

    function resetCta() {
      self.submitting = false;
      cta.disabled = false;
      ctaText.removeAttribute('hidden');
      ctaLoading.setAttribute('hidden', '');
    }

    core.setPendingFree(data.variantId);
    var cartPromise = core.addToCart(data.variantId, true);
    core.klaviyoSubscribe(email);
    var netlifyPromise = core.withTimeout(
      core.netlifySubmit(email, data.variantId, data.productTitle), 8000
    ).catch(function (err) {
      console.error('Netlify function error:', err);
      return null;
    });

    cartPromise
      .then(function () {
        core.setMember(email);
        self.tagCampaign();
        self.anySampleInCart = true;
        self.cartSampleVariantIds[data.variantId] = true;
        self.deriveCredit();
        resetCta();
        self.showSuccessState();

        // Kept on the instance so go-cart can wait for it: navigating to
        // the basket mid-heal shows the sample at £2 for a beat before the
        // reconciler repaints it free
        self.pendingHeal = netlifyPromise
          .then(function (result) {
            var code = result && result.discount_code;
            core.setStoredCode(code);
            return core.applyDiscountCode(code);
          })
          .then(function () {
            if (window.knpReconcile) window.knpReconcile(0);
          });
      })
      .catch(function (err) {
        self.showToast('Something went wrong. Please try again.');
        console.error('Sample quick buy add failed:', err);
        resetCta();
      });
  };

  QuickBuy.prototype.onSkipOffer = function () {
    var self = this;
    this.closeSheet();
    setTimeout(function () { self.addSample(false); }, 200);
  };

  QuickBuy.prototype.showSuccessState = function () {
    this.sheetForm.setAttribute('hidden', '');
    this.sheetPaid.setAttribute('hidden', '');
    this.sheetSuccess.removeAttribute('hidden');

    var subtitle = this.sheetSuccess.querySelector('[data-success-subtitle]');
    if (subtitle) subtitle.textContent = 'Discount applies at checkout';

    this.updateButtonLabels();
  };

  QuickBuy.prototype.showEmailError = function () {
    if (this.emailError) this.emailError.removeAttribute('hidden');
    if (this.emailInput) this.emailInput.classList.add('sf-sheet__input--error');
  };

  QuickBuy.prototype.clearEmailError = function () {
    if (this.emailError) this.emailError.setAttribute('hidden', '');
    if (this.emailInput) this.emailInput.classList.remove('sf-sheet__input--error');
  };

  QuickBuy.prototype.showToast = function (message) {
    if (!this.toast) return;
    var self = this;
    this.toast.textContent = message;
    this.toast.removeAttribute('hidden');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () {
      self.toast.setAttribute('hidden', '');
    }, 4000);
  };

  /* ── Boot ── */

  function boot() {
    var root = document.getElementById('SampleQuickBuy');
    if (root && !root.dataset.bound) {
      root.dataset.bound = 'true';
      new QuickBuy(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
